import { useEffect, useMemo, useState } from "react";
import {
  IConteudo,
  placeHolderContent,
} from "../../../Interfaces/model/IConteudo";
import {
  INoticia,
  INoticiaPost,
  placeholderNoticia,
  todayDate,
} from "../../../Interfaces/model/INoticia";
import { PurpleButton } from "../../../components/buttons/purpleButton";
import { CarrosselPreview } from "../../../components/carrosselPreview";
import { ImageUpload } from "../../../components/imageUpload";
import { TextBox } from "../../../components/textBox";
import noticiaService from "../../../services/noticiaService";
import { getCarrosselParagrafo } from "../../../utils/carrosselPreviewUtils";
import { Notify } from "../../../utils/notify";
import { scrollUp } from "../../../utils/pageUtils";
import { Preview } from "../../news/components/previews";
import { DeleteContentSummary } from "./components/DeleteContentSummary";
import { UpdateContent } from "./components/UpdateContent";
import {
  ContentAndPreviewContainer,
  ContentContainer,
  ContentCreationContainer,
  PreviewBoxContainer,
  PreviewSection,
} from "./styles";

export const Post = () => {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [city, setCity] = useState("");
  const category = 0;
  const [subtitle, setSubtitle] = useState("");
  const [paragraph, setParagraph] = useState("");
  const [image, setImage] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const [selectedIndexDelete, setSelectedIndexDelete] = useState<number>(0);
  const [selectedIndexUpdate, setSelectedIndexUpdate] = useState<number>(0);

  const [arrayOfContent, setArrayOfContent] = useState<IConteudo[]>([]);

  const [preview, setPreview] = useState<INoticia>(placeholderNoticia);
  const [isMainImageUploading, setIsMainImageUploading] = useState(false);
  const [isContentImageUploading, setIsContentImageUploading] = useState(false);
  const [isUpdateImageUploading, setIsUpdateImageUploading] = useState(false);
  const isImageUploading =
    isMainImageUploading ||
    isContentImageUploading ||
    isUpdateImageUploading;

  const carrosselPreview = useMemo(
    () => ({
      titulo: title,
      texto: getCarrosselParagrafo(arrayOfContent, paragraph),
      imagem: mainImage,
    }),
    [title, arrayOfContent, paragraph, mainImage]
  );

  useEffect(() => {
    const previewUpdated: INoticia = {
      id: 1,
      data: todayDate(),
      autor: author,
      categoria: { id: category, nome: "Categoria" },
      cidade: city,
      imagem: mainImage,
      conteudo: arrayOfContent,
      titulo: title,
      video: "",
    };

    setPreview(previewUpdated);
  }, [author, category, city, mainImage, arrayOfContent, title]);

  useEffect(() => {
    scrollUp();
  }, []);

  function pushContentToArray() {
    if (isImageUploading) {
      Notify.warning("Aguarde o envio da imagem antes de adicionar conteúdo.");
      return;
    }

    const content: IConteudo = {
      subtitulo: subtitle,
      paragrafo: paragraph,
      imagem: image,
      video: videoUrl,
    };
    if (paragraph.length !== 0) {
      if (window.confirm("Deseja realmente adicionar esse conteúdo?")) {
        setArrayOfContent((prevState) => [...prevState, content]);
        setSubtitle("");
        setImage("");
        setParagraph("");
        setVideoUrl("");
      }
    } else {
      Notify.warning("O parágrafo não pode estar vazio");
    }
  }

  async function postNew(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isImageUploading) {
      Notify.warning("Aguarde o envio das imagens antes de publicar.");
      return;
    }

    const newsToPost: INoticiaPost = {
      autor: author,
      categoria: { id: category },
      cidade: city,
      imagem: mainImage,
      conteudo: arrayOfContent,
      titulo: title,
      video: "",
    };

    if (newsToPost.conteudo.length > 0) {
      if (window.confirm("Deseja realmente fazer essa postagem?")) {
        try {
          await noticiaService.postNoticia(newsToPost);
          Notify.success("Notícia criada com sucesso!");
        } catch (e) {
          Notify.failure("Não foi possível criar a notícia, tente novamente!");
        }
      }
    } else {
      Notify.warning("Por favor, adicione algum conteúdo");
    }
  }

  function deleteSelectedContent() {
    if (isImageUploading) {
      Notify.warning("Aguarde o envio da imagem antes de remover conteúdo.");
      return;
    }

    if (window.confirm("Deseja realmente remover esse conteúdo?")) {
      const newArray = arrayOfContent.filter(
        (_, i) => i !== selectedIndexDelete
      );
      if (arrayOfContent.length - 1 === selectedIndexDelete) {
        if (arrayOfContent.length - 2 >= 0) {
          setSelectedIndexDelete(arrayOfContent.length - 2);
        } else {
          setSelectedIndexDelete(0);
        }
      }
      if (arrayOfContent.length - 1 === selectedIndexUpdate) {
        if (arrayOfContent.length - 2 >= 0) {
          setSelectedIndexUpdate(arrayOfContent.length - 2);
        } else {
          setSelectedIndexUpdate(0);
        }
      }
      setTimeout(() => {
        setArrayOfContent(newArray);
      }, 100);
    }
  }

  return (
    <ContentAndPreviewContainer>
      <ContentContainer onSubmit={postNew}>
        <TextBox
          required
          maxLength={100}
          value={author}
          title="Autor"
          onChangeText={setAuthor}
        />
        <TextBox
          required
          maxLength={120}
          value={title}
          title="Título"
          onChangeText={setTitle}
        />
        <ImageUpload
          required
          value={mainImage}
          label="Imagem principal"
          category="NOTICIAS"
          onChange={setMainImage}
          onUploadingChange={setIsMainImageUploading}
        />

        <select value={city} onChange={(e) => setCity(e.target.value)} required>
          <option value="">Selecione uma localidade</option>
          <option value="petropolis">Petrópolis</option>
          <option value="rio-de-janeiro">Rio de Janeiro</option>
          <option value="global">Global</option>
        </select>

        <ContentCreationContainer>
          <h4>Criação de conteúdo</h4>
          <TextBox
            value={subtitle}
            maxLength={255}
            title="Subtítulo"
            onChangeText={setSubtitle}
          />
          <TextBox
            value={paragraph}
            maxLength={3000}
            height="400px"
            title="Parágrafo"
            onChangeText={setParagraph}
          />
          <ImageUpload
            value={image}
            label="Imagem do conteúdo"
            category="CONTEUDO"
            onChange={setImage}
            onUploadingChange={setIsContentImageUploading}
          />
          <TextBox
            value={videoUrl}
            maxLength={255}
            title="Link do vídeo"
            onChangeText={setVideoUrl}
          />
          <PurpleButton
            onClick={pushContentToArray}
            disabled={isImageUploading}
            title="Adicionar novo conteúdo"
          />
        </ContentCreationContainer>

        <DeleteContentSummary
          deleteOnClick={deleteSelectedContent}
          showDeleteContainer={arrayOfContent.length > 0}
          arrayOfContent={
            arrayOfContent.length > 0 ? arrayOfContent : [placeHolderContent]
          }
          selectedIndex={selectedIndexDelete}
          setSelectedIndex={setSelectedIndexDelete}
          disabled={isImageUploading}
        />
        <UpdateContent
          setArrayOfContent={setArrayOfContent}
          showDeleteContainer={arrayOfContent.length > 0}
          arrayOfContent={
            arrayOfContent.length > 0 ? arrayOfContent : [placeHolderContent]
          }
          selectedIndex={selectedIndexUpdate}
          setSelectedIndex={setSelectedIndexUpdate}
          isUploading={isImageUploading}
          onUploadingChange={setIsUpdateImageUploading}
        />
        <PurpleButton
          title="Criar postagem"
          disabled={isImageUploading}
          submit
        />
      </ContentContainer>
      <PreviewBoxContainer showPreview={true}>
        <PreviewSection>
          <h3>Pré-visualização do carrossel</h3>
          <p>
            Ao publicar, a notícia entrará automaticamente no carrossel da home
            quando houver imagem principal. Título e parágrafo abaixo refletem o
            card exibido na página inicial.
          </p>
          <CarrosselPreview
            titulo={carrosselPreview.titulo}
            texto={carrosselPreview.texto}
            imagem={carrosselPreview.imagem}
          />
        </PreviewSection>
        <PreviewSection>
          <h3>Pré-visualização da notícia</h3>
          <Preview news={preview} />
        </PreviewSection>
      </PreviewBoxContainer>
    </ContentAndPreviewContainer>
  );
};
